import React, { useEffect, useState, createContext, useContext } from 'react';
import { checkRateLimit, isSpamEmail } from '../../utils/antiSpam';
// Define step IDs as constants
export const STEP_IDS = {
  WELCOME: 0,
  PROJECT_TYPE: 1,
  DIMENSIONS: 2,
  COLOR: 3,
  ROOF_MATERIAL: 4,
  EXTRA_ELEMENTS: 5,
  SCHUIFWAND_TYPE: 6,
  SCHUIFWAND_COLOR: 7,
  CARPORT_ROOF: 8,
  CARPORT_COLOR: 9,
  TUINBESTRATING_DETAILS: 10,
  SCHUTTING_DETAILS: 11,
  ZANDCEMENT_DETAILS: 12,
  PERSONAL_DETAILS: 13,
  SUMMARY: 14
};
// Define the form data type
export interface FormData {
  projectTypes: string[];
  gardenWidth: string;
  verandaDepth: string;
  color: string;
  roofMaterial: string;
  ledSpots: number;
  leftSide: string;
  rightSide: string;
  frontSide: string;
  schuifwandType: string;
  schuifwandColor: string;
  carportRoof: string;
  carportColor: string;
  // New fields for tuinbestrating
  tuinbestratingLength: string;
  tuinbestratingWidth: string;
  tuinbestratingRemarks: string;
  // New fields for schutting
  schuttingLength: string;
  schuttingHeight: string;
  schuttingRemarks: string;
  // New fields for zandcement
  zandcementLength: string;
  zandcementWidth: string;
  zandcementRemarks: string;
  // Personal details
  name: string;
  email: string;
  phone: string;
  message: string;
  consent: boolean;
}
// Define the context type
interface FormContextType {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  visibleSteps: number[];
  formSubmitted: boolean;
  setFormSubmitted: (submitted: boolean) => void;
  direction: 'forward' | 'backward';
  setDirection: (direction: 'forward' | 'backward') => void;
  isAnimating: boolean;
  setIsAnimating: (isAnimating: boolean) => void;
  resetForm: () => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  handleProjectTypeToggle: (type: string) => void;
  handleOptionSelect: (field: string, value: string) => void;
  handleNumberChange: (field: string, value: number) => void;
  isCurrentStepValid: () => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  getCurrentStepIndex: () => number;
  getTotalSteps: () => number;
  calculateProgress: () => number;
  getStepNumber: () => number;
  hasStructureOption: () => boolean;
  hasRoofOption: () => boolean;
  hasSchuifwanden: () => boolean;
  hasCarport: () => boolean;
  hasZonwering: () => boolean;
  hasTuinbestrating: () => boolean;
  hasSchutting: () => boolean;
  hasZandcement: () => boolean;
  getStepGuideText: () => string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}
// Create the context with default values
const FormContext = createContext<FormContextType | undefined>(undefined);
// Define the background images
const backgroundImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/31qoFLD89xVNfcg4Xki5Xk/491436229_17850212535441316_3063343797506131228_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/6hFDuMkKtrrN66CxmjRtGR/488225866_17848235589441316_3303414385176099418_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/7nk9VQSmML7hwtXboprLaz/IMG-20250423-WA0012.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/bbqzAX3HGa1qV4DnaGXJAq/IMG-20250423-WA0011.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/1kkTSutEG3n1jaMh5paBAH/487922126_17848235598441316_7489604228845729630_n.jpg'];
// Create the provider component
export const FormProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // Form state management
  const [currentStep, setCurrentStep] = useState(STEP_IDS.WELCOME);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    // Project details
    projectTypes: [],
    // Dimensions
    gardenWidth: '',
    verandaDepth: '',
    // Conditional fields for structures
    color: '',
    roofMaterial: 'glas-helder',
    // LED lighting
    ledSpots: 10,
    // Extra elements for structures
    leftSide: '',
    rightSide: '',
    frontSide: '',
    // Schuifwanden specific
    schuifwandType: '',
    schuifwandColor: '',
    // Carport specific
    carportRoof: '',
    carportColor: '',
    // Tuinbestrating specific
    tuinbestratingLength: '',
    tuinbestratingWidth: '',
    tuinbestratingRemarks: '',
    // Schutting specific
    schuttingLength: '',
    schuttingHeight: '',
    schuttingRemarks: '',
    // Zandcement specific
    zandcementLength: '',
    zandcementWidth: '',
    zandcementRemarks: '',
    // Personal details
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });
  // Animation states
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  // Define which steps should be shown based on selections
  const [visibleSteps, setVisibleSteps] = useState([STEP_IDS.WELCOME, STEP_IDS.PROJECT_TYPE]);
  // Helper functions to check project types
  const hasStructureOption = () => {
    return formData.projectTypes.some(type => ['veranda', 'overkapping', 'tuinkamer'].includes(type));
  };
  const hasRoofOption = () => {
    return formData.projectTypes.some(type => ['veranda', 'overkapping'].includes(type));
  };
  const hasSchuifwanden = () => {
    return formData.projectTypes.includes('schuifwanden');
  };
  const hasCarport = () => {
    return formData.projectTypes.includes('carport');
  };
  const hasZonwering = () => {
    return formData.projectTypes.includes('zonwering');
  };
  const hasTuinbestrating = () => {
    return formData.projectTypes.includes('tuinbestrating');
  };
  const hasSchutting = () => {
    return formData.projectTypes.includes('schutting');
  };
  const hasZandcement = () => {
    return formData.projectTypes.includes('zandcementvloer');
  };
  // Update visible steps whenever project types change
  useEffect(() => {
    if (formData.projectTypes.length === 0) {
      setVisibleSteps([STEP_IDS.WELCOME, STEP_IDS.PROJECT_TYPE]);
      return;
    }
    const steps = [STEP_IDS.WELCOME, STEP_IDS.PROJECT_TYPE];
    // Add structure-related steps
    if (hasStructureOption()) {
      steps.push(STEP_IDS.DIMENSIONS);
      steps.push(STEP_IDS.COLOR);
      if (hasRoofOption()) {
        steps.push(STEP_IDS.ROOF_MATERIAL);
      }
      steps.push(STEP_IDS.EXTRA_ELEMENTS);
    }
    // Add schuifwanden steps
    if (hasSchuifwanden()) {
      steps.push(STEP_IDS.SCHUIFWAND_TYPE);
      steps.push(STEP_IDS.SCHUIFWAND_COLOR);
    }
    // Add carport steps
    if (hasCarport()) {
      steps.push(STEP_IDS.CARPORT_ROOF);
      steps.push(STEP_IDS.CARPORT_COLOR);
    }
    // Add tuinbestrating steps
    if (hasTuinbestrating()) {
      steps.push(STEP_IDS.TUINBESTRATING_DETAILS);
    }
    // Add schutting steps
    if (hasSchutting()) {
      steps.push(STEP_IDS.SCHUTTING_DETAILS);
    }
    // Add zandcement steps
    if (hasZandcement()) {
      steps.push(STEP_IDS.ZANDCEMENT_DETAILS);
    }
    // Add common final steps
    steps.push(STEP_IDS.PERSONAL_DETAILS);
    steps.push(STEP_IDS.SUMMARY);
    setVisibleSteps(steps);
  }, [formData.projectTypes]);
  // Find the current step index in the visible steps array
  const getCurrentStepIndex = () => {
    return visibleSteps.indexOf(currentStep);
  };
  // Get the total number of steps (excluding welcome)
  const getTotalSteps = () => {
    // We subtract 1 because we don't count the welcome screen in the progress
    return visibleSteps.length - 1;
  };
  // Calculate current progress percentage
  const calculateProgress = () => {
    const currentIndex = getCurrentStepIndex();
    // If we're on the welcome screen, progress is 0
    if (currentIndex === 0) return 0;
    // If we're on the summary screen, progress is 100%
    if (currentStep === STEP_IDS.SUMMARY) return 100;
    // Otherwise, calculate progress (current step / total steps)
    // We subtract 1 from currentIndex because we don't count the welcome screen
    return Math.round((currentIndex - 1) / (getTotalSteps() - 1) * 100);
  };
  // Get the step number to display
  const getStepNumber = () => {
    const currentIndex = getCurrentStepIndex();
    // If we're on the welcome screen, step number is 0
    if (currentIndex === 0) return 0;
    // Otherwise, return the step number (excluding welcome screen)
    return currentIndex;
  };
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target as HTMLInputElement;
    if (type === 'number' || name === 'gardenWidth' || name === 'verandaDepth' || name === 'tuinbestratingLength' || name === 'tuinbestratingWidth' || name === 'schuttingLength' || name === 'schuttingHeight' || name === 'zandcementLength' || name === 'zandcementWidth') {
      // For number inputs, store as string but ensure it's a valid number
      const numValue = value === '' ? '' : value;
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  // Update form data with partial updates
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };
  // Handle multi-selection for project types
  const handleProjectTypeToggle = (type: string) => {
    setFormData(prev => {
      const currentTypes = [...prev.projectTypes];
      if (currentTypes.includes(type)) {
        return {
          ...prev,
          projectTypes: currentTypes.filter(item => item !== type)
        };
      } else {
        return {
          ...prev,
          projectTypes: [...currentTypes, type]
        };
      }
    });
  };
  // Handle single option selection
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Handle number input changes
  const handleNumberChange = (field: string, value: number) => {
    if (!isNaN(value)) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  // Navigation functions
  const goToNextStep = () => {
    const currentIndex = getCurrentStepIndex();
    // If we're at the last step, don't go further
    if (currentIndex >= visibleSteps.length - 1) return;
    // Set animation direction and start animating
    setDirection('forward');
    setIsAnimating(true);
    // After animation starts, change the step
    setTimeout(() => {
      // Go to the next visible step
      setCurrentStep(visibleSteps[currentIndex + 1]);
      // End animation after a short delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 300);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const goToPreviousStep = () => {
    const currentIndex = getCurrentStepIndex();
    // If we're at the first step, don't go back
    if (currentIndex <= 0) return;
    // Set animation direction and start animating
    setDirection('backward');
    setIsAnimating(true);
    // After animation starts, change the step
    setTimeout(() => {
      // Go to the previous visible step
      setCurrentStep(visibleSteps[currentIndex - 1]);
      // End animation after a short delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 300);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const resetForm = () => {
    setCurrentStep(STEP_IDS.WELCOME);
    setFormSubmitted(false);
    setFormData({
      projectTypes: [],
      gardenWidth: '',
      verandaDepth: '',
      color: '',
      roofMaterial: 'glas-helder',
      ledSpots: 10,
      leftSide: '',
      rightSide: '',
      frontSide: '',
      schuifwandType: '',
      schuifwandColor: '',
      carportRoof: '',
      carportColor: '',
      tuinbestratingLength: '',
      tuinbestratingWidth: '',
      tuinbestratingRemarks: '',
      schuttingLength: '',
      schuttingHeight: '',
      schuttingRemarks: '',
      zandcementLength: '',
      zandcementWidth: '',
      zandcementRemarks: '',
      name: '',
      email: '',
      phone: '',
      message: '',
      consent: false
    });
  };
  // Form submission handler
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Rate limiting check
    if (!checkRateLimit('offerte')) {
      console.error('Rate limit exceeded');
      // Handle rate limit error
      return;
    }
    // Helper function to get readable project type names
    const getProjectTypeNames = (types: string[]) => {
      const projectTypeNames: {
        [key: string]: string;
      } = {
        veranda: 'Veranda',
        overkapping: 'Overkapping',
        tuinkamer: 'Tuinkamer',
        schuifwanden: 'Schuifwanden',
        carport: 'Carport',
        tuinbestrating: 'Tuinbestrating',
        schutting: 'Schutting',
        zandcementvloer: 'Zandcementvloer',
        zonwering: 'Zonwering'
      };
      return types.map(type => projectTypeNames[type] || type).join(', ');
    };
    // Create HTML email content
    const emailHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <h2 style="color: #CDA735; border-bottom: 2px solid #CDA735; padding-bottom: 10px;">Nieuwe offerte aanvraag</h2>
        <h3 style="margin-top: 20px; color: #555;">Klantgegevens</h3>
        <p><strong>Naam:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Telefoon:</strong> ${formData.phone}</p>
        ${formData.message ? `<p><strong>Toelichting:</strong> ${formData.message}</p>` : ''}
        <h3 style="margin-top: 20px; color: #555;">Project details</h3>
        <p><strong>Producten:</strong> ${getProjectTypeNames(formData.projectTypes)}</p>
        ${hasStructureOption() ? `
        <h4 style="margin-top: 15px; color: #666;">Afmetingen</h4>
        <p><strong>Tuin breedte:</strong> ${formData.gardenWidth} cm</p>
        <p><strong>Veranda diepte:</strong> ${formData.verandaDepth} cm</p>
        ` : ''}
        ${hasStructureOption() && formData.color ? `
        <h4 style="margin-top: 15px; color: #666;">Kleur constructie</h4>
        <p>${formData.color === 'antraciet' ? 'Antraciet (RAL 7016)' : formData.color === 'gitzwart' ? 'Gitzwart (RAL 9005)' : formData.color === 'metallic-grijs' ? 'Metallic grijs (RAL 9007)' : 'Verkeerswit (RAL 9016)'}</p>
        ` : ''}
        ${hasRoofOption() && formData.roofMaterial ? `
        <h4 style="margin-top: 15px; color: #666;">Dakbedekking</h4>
        <p>${formData.roofMaterial === 'glas-helder' ? 'Glas helder' : formData.roofMaterial === 'glas-getint' ? 'Glas getint' : formData.roofMaterial === 'polycarbonaat-opaal' ? 'Polycarbonaat opaal' : 'Polycarbonaat helder'}</p>
        ` : ''}
        ${hasStructureOption() ? `
        <h4 style="margin-top: 15px; color: #666;">LED Verlichting</h4>
        <p>${formData.ledSpots === 10 ? '10 LED spots (standaard inbegrepen)' : `${formData.ledSpots} LED spots (10 standaard + ${formData.ledSpots - 10} extra)`}</p>
        ` : ''}
        ${hasStructureOption() ? `
        <h4 style="margin-top: 15px; color: #666;">Extra elementen</h4>
        <p><strong>Zijwand links:</strong> ${formData.leftSide === 'none' ? 'Geen zijkant' : formData.leftSide === 'aluminium-spie' ? 'Aluminium + spie polycarbonaat' : formData.leftSide === 'aluminium-compleet' ? 'Aluminium compleet' : formData.leftSide === 'glas' ? 'Glas' : 'Glazen schuifwand'}</p>
        <p><strong>Zijwand rechts:</strong> ${formData.rightSide === 'none' ? 'Geen zijkant' : formData.rightSide === 'aluminium-spie' ? 'Aluminium + spie polycarbonaat' : formData.rightSide === 'aluminium-compleet' ? 'Aluminium compleet' : formData.rightSide === 'glas' ? 'Glas' : 'Glazen schuifwand'}</p>
        <p><strong>Voorkant:</strong> ${formData.frontSide === 'open' ? 'Open' : formData.frontSide === 'schuifpui' ? 'Schuifpui' : 'Glazen schuifwand'}</p>
        ` : ''}
        ${hasSchuifwanden() && formData.schuifwandType ? `
        <h4 style="margin-top: 15px; color: #666;">Schuifwand details</h4>
        <p><strong>Type:</strong> ${formData.schuifwandType === 'aluminium' ? 'Aluminium schuifwand' : formData.schuifwandType === 'glas' ? 'Glazen schuifwand' : 'Vouwwand'}</p>
        <p><strong>Kleur:</strong> ${formData.schuifwandColor === 'antraciet' ? 'Antraciet (RAL 7016)' : formData.schuifwandColor === 'gitzwart' ? 'Gitzwart (RAL 9005)' : formData.schuifwandColor === 'metallic-grijs' ? 'Metallic grijs (RAL 9007)' : 'Verkeerswit (RAL 9016)'}</p>
        ` : ''}
        ${hasCarport() && formData.carportRoof ? `
        <h4 style="margin-top: 15px; color: #666;">Carport details</h4>
        <p><strong>Dakbedekking:</strong> ${formData.carportRoof === 'polycarbonaat-helder' ? 'Polycarbonaat helder' : formData.carportRoof === 'polycarbonaat-opaal' ? 'Polycarbonaat opaal' : 'Glas helder'}</p>
        <p><strong>Kleur:</strong> ${formData.carportColor === 'antraciet' ? 'Antraciet (RAL 7016)' : formData.carportColor === 'gitzwart' ? 'Gitzwart (RAL 9005)' : formData.carportColor === 'metallic-grijs' ? 'Metallic grijs (RAL 9007)' : 'Verkeerswit (RAL 9016)'}</p>
        ` : ''}
        ${hasTuinbestrating() ? `
        <h4 style="margin-top: 15px; color: #666;">Tuinbestrating details</h4>
        <p><strong>Lengte:</strong> ${formData.tuinbestratingLength} cm</p>
        <p><strong>Breedte:</strong> ${formData.tuinbestratingWidth} cm</p>
        ${formData.tuinbestratingRemarks ? `<p><strong>Opmerkingen:</strong> ${formData.tuinbestratingRemarks}</p>` : ''}
        ` : ''}
        ${hasSchutting() ? `
        <h4 style="margin-top: 15px; color: #666;">Schutting details</h4>
        <p><strong>Lengte:</strong> ${formData.schuttingLength} cm</p>
        <p><strong>Hoogte:</strong> ${formData.schuttingHeight} cm</p>
        ${formData.schuttingRemarks ? `<p><strong>Opmerkingen:</strong> ${formData.schuttingRemarks}</p>` : ''}
        ` : ''}
        ${hasZandcement() ? `
        <h4 style="margin-top: 15px; color: #666;">Zandcement vloer details</h4>
        <p><strong>Lengte:</strong> ${formData.zandcementLength} cm</p>
        <p><strong>Breedte:</strong> ${formData.zandcementWidth} cm</p>
        ${formData.zandcementRemarks ? `<p><strong>Opmerkingen:</strong> ${formData.zandcementRemarks}</p>` : ''}
        ` : ''}
        <p style="margin-top: 20px; font-size: 12px; color: #777;">Dit bericht is verzonden via het offerte aanvraag formulier op veranovaklussen.nl</p>
      </div>
    `;
    // Create webhook payload
    const webhookPayload = {
      emailSubject: 'Nieuwe offerte aanvraag - Veranova Klussen',
      emailTo: 'info@veranovaklussen.nl',
      formType: 'offerte',
      formData: {
        ...formData,
        timestamp: Date.now()
      },
      emailHTML: emailHTML
    };
    const webhookUrl = 'https://hook.eu2.make.com/e9ytbwhleok4r9szhhz2ltro238af7as';
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      if (!response.ok) {
        throw new Error('Server responded with an error');
      }
      console.log('Offerte aanvraag webhook sent successfully:', webhookPayload);
      setFormSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      console.error('Webhook error:', error);
    }
  };
  // Check if the current step is enabled (all required fields filled)
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case STEP_IDS.PROJECT_TYPE:
        return formData.projectTypes.length > 0;
      case STEP_IDS.DIMENSIONS:
        return formData.gardenWidth !== '' && formData.verandaDepth !== '';
      case STEP_IDS.COLOR:
        return !!formData.color;
      case STEP_IDS.ROOF_MATERIAL:
        return !!formData.roofMaterial;
      case STEP_IDS.EXTRA_ELEMENTS:
        return true;
      // Always valid as defaults are set
      case STEP_IDS.SCHUIFWAND_TYPE:
        return !!formData.schuifwandType;
      case STEP_IDS.SCHUIFWAND_COLOR:
        return !!formData.schuifwandColor;
      case STEP_IDS.CARPORT_ROOF:
        return !!formData.carportRoof;
      case STEP_IDS.CARPORT_COLOR:
        return !!formData.carportColor;
      case STEP_IDS.TUINBESTRATING_DETAILS:
        return formData.tuinbestratingLength !== '' && formData.tuinbestratingWidth !== '';
      case STEP_IDS.SCHUTTING_DETAILS:
        return formData.schuttingLength !== '' && formData.schuttingHeight !== '';
      case STEP_IDS.ZANDCEMENT_DETAILS:
        return formData.zandcementLength !== '' && formData.zandcementWidth !== '';
      case STEP_IDS.PERSONAL_DETAILS:
        return formData.name && formData.email && formData.phone && formData.consent;
      default:
        return true;
    }
  };
  // Get step guide text based on current step
  const getStepGuideText = () => {
    switch (currentStep) {
      case STEP_IDS.PROJECT_TYPE:
        return 'Selecteer de producten die u interesseren. U kunt meerdere opties kiezen om te combineren.';
      case STEP_IDS.DIMENSIONS:
        return 'Vul de afmetingen in centimeters in. Dit helpt ons een nauwkeurige offerte te maken.';
      case STEP_IDS.COLOR:
        return 'Kies een kleur die past bij uw woning. De meest populaire keuze is antraciet.';
      case STEP_IDS.ROOF_MATERIAL:
        return 'Het dakmateriaal bepaalt lichtinval en isolatie. Glas biedt maximaal licht, polycarbonaat is lichter en goedkoper.';
      case STEP_IDS.EXTRA_ELEMENTS:
        return 'Personaliseer uw project met extra elementen zoals zijwanden en LED verlichting.';
      case STEP_IDS.TUINBESTRATING_DETAILS:
        return 'Vul de afmetingen van het te bestraten oppervlak in. Verdere details bespreken we telefonisch.';
      case STEP_IDS.SCHUTTING_DETAILS:
        return 'Vul de lengte en hoogte van de schutting in. Verdere details bespreken we telefonisch.';
      case STEP_IDS.ZANDCEMENT_DETAILS:
        return 'Vul de afmetingen van de vloer in. Verdere details bespreken we telefonisch.';
      case STEP_IDS.PERSONAL_DETAILS:
        return 'Uw gegevens worden alleen gebruikt om contact met u op te nemen over uw offerte.';
      default:
        return '';
    }
  };
  // Scroll to top when changing steps
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);
  // Create the context value
  const contextValue: FormContextType = {
    currentStep,
    setCurrentStep,
    formData,
    updateFormData,
    visibleSteps,
    formSubmitted,
    setFormSubmitted,
    direction,
    setDirection,
    isAnimating,
    setIsAnimating,
    resetForm,
    handleSubmit,
    handleProjectTypeToggle,
    handleOptionSelect,
    handleNumberChange,
    isCurrentStepValid,
    goToNextStep,
    goToPreviousStep,
    getCurrentStepIndex,
    getTotalSteps,
    calculateProgress,
    getStepNumber,
    hasStructureOption,
    hasRoofOption,
    hasSchuifwanden,
    hasCarport,
    hasZonwering,
    hasTuinbestrating,
    hasSchutting,
    hasZandcement,
    getStepGuideText,
    handleChange
  };
  return <FormContext.Provider value={contextValue} data-id="element-755">{children}</FormContext.Provider>;
};
// Create a custom hook to use the form context
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
// Export the background images
export { backgroundImages };